import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBagIcon, CalendarIcon, StarIcon, SparklesIcon, MessageSquareIcon, BookOpenIcon, BoxIcon } from 'lucide-react';
import CustomPromotionalBannerCarousel from '../components/UI/CustomPromotionalBannerCarousel';
import GraphicsCarousel from '../components/Graphics/GraphicsCarousel';
import StoryStackHero from '../components/Home/StoryStackHero';
import DiscoveryRow from '../components/Home/DiscoveryRow';
import { FeaturedMerchCard } from '../components/Home/FeaturedMerchCard';
import { FeaturedBookBoxCard } from '../components/Home/FeaturedBookBoxCard';
import { EventTeaserCard } from '../components/Home/EventTeaserCard';
import { TroveArticleCard } from '../components/Home/TroveArticleCard';
import { NewsletterSection } from '../components/Home/NewsletterSection';
import { VendorCTASection } from '../components/Home/VendorCTASection';
import AuthorSpotlightCarousel from '../components/Home/AuthorSpotlightCarousel';
import { Carousel } from '../components/shared/Carousel';
import { SectionHeader } from '../components/shared/SectionHeader';
import { samplePromotionalGraphics } from '../data/samplePromotionalGraphics';
import { allBooks } from '../data/booksData';
import { getBooksForDiscoveryRow } from '../data/discoveryRowsData';
import { brandHomeData } from '../data/brandHomeData';
import { useBrand } from '../hooks/useBrand';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { handleImgError } from '../utils/imageUtils';

/**
 * Home — one layout for both platforms. Copy comes from the brand content,
 * rows come from the brand's home dataset, colours from the brand theme.
 */
const Home = () => {
  const {
    id: brandId,
    theme,
    content
  } = useBrand();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);
  const data = brandHomeData[brandId];
  const featuredGraphics = samplePromotionalGraphics.slice(0, 8);

  // Dynamic personalised row built from the moods picked in the Story Stack
  const personalizedBooks = selectedMoods.length > 0 ? getBooksForDiscoveryRow(allBooks, {
    tags: selectedMoods.map(m => m.toLowerCase().replace(/\s+/g, '-'))
  }) : [];
  return <div className={`w-full ${dk.page}`} data-id="element-9341" data-mp="1fm5xwb2tpf4t">
      <CustomPromotionalBannerCarousel data-id="element-9342" data-mp="23b8b96qfclti" />

      {/* Tagline + Story Stack Hero */}
      <section className={`pt-8 pb-4 sm:pt-10 sm:pb-6 ${dk.page}`} data-id="element-9343" data-mp="23db9uf5n7n9u">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9344" data-mp="28906oaqa2q2g">
          <div className="text-center mb-6 sm:mb-8" data-id="element-9345" data-mp="1tlx8s2y1mfel">
            <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-1.5 sm:mb-2 ${theme.accent.headline}`} data-id="element-9346" data-mp="1tjb01zzf1tus">
              {content.home.tagline}
            </h2>
            <p className={`text-sm sm:text-base ${dk.textSecondary}`} data-id="element-9347" data-mp="2m8rq4qzkcynu">
              {content.home.subtitle}
            </p>
          </div>
          <StoryStackHero onMoodSelect={setSelectedMoods} data-id="element-9348" data-mp="flkbvfjpnxc4" />
        </div>
      </section>

      {selectedMoods.length > 0 && personalizedBooks.length > 0 && <DiscoveryRow row={{
      id: 'personalized-stack',
      title: 'Based on Your Story Stack',
      description: `Books matching: ${selectedMoods.join(', ')}`,
      emoji: '🎯',
      type: 'personalized',
      filters: {},
      accentColor: theme.accent.icon
    }} books={personalizedBooks} data-id="element-9349" data-mp="amh6ftztjp3m" />}

      {/* Metadata-driven discovery feed, alternating backgrounds for rhythm */}
      {data.discoveryRows.map((row, index) => <div key={row.id} className={index % 2 === 0 ? dk.surface : dk.page} data-id="element-9350" data-mp="ojht5e1rqah" data-mpi={index}>
          <DiscoveryRow row={row} books={getBooksForDiscoveryRow(allBooks, row.filters)} data-id="element-9351" data-mp="qnvug5nu5oh" />
        </div>)}

      <section className={`py-12 ${dk.page}`} data-id="element-9352" data-mp="1c488a6qif7g1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9353" data-mp="1c3ghd97uxxw3">
          <SectionHeader title="Book Graphics" subtitle="Explore author-created visuals highlighting tropes, spice levels, and book features" icon={BookOpenIcon} action={{
          label: 'View all',
          to: '/books/graphics'
        }} data-id="element-9354" data-mp="27ey7jqa21nvf" />
          <GraphicsCarousel graphics={featuredGraphics} aspectRatio="portrait" showOverlay={true} data-id="element-9355" data-mp="2mboz5ddhlvah" />
        </div>
      </section>

      <section className={`py-12 ${dk.surface}`} data-id="element-9356" data-mp="2qawmt6i0vrl1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9357" data-mp="2vaj35ot5dny2">
          <SectionHeader title="Author Spotlight" icon={SparklesIcon} iconTone="warm" action={{
          label: 'View all',
          to: '/authors'
        }} data-id="element-9358" data-mp="302u4mw3jdhsq" />
          <AuthorSpotlightCarousel authors={data.spotlightAuthors} data-id="element-9359" data-mp="3a10313q4w5su" />
        </div>
      </section>

      <section className={`py-12 ${dk.page}`} data-id="element-9360" data-mp="3e224zsbfqvyi">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9361" data-mp="3e34rzp5n0e95">
          <SectionHeader title="Recent Reader Reviews" icon={MessageSquareIcon} className="mb-8" data-id="element-9362" data-mp="12fhvb7qnbf8l" />
          <Carousel label="Recent reader reviews" data-id="element-9363" data-mp="yc3be5zl6epv">
            {data.readerReviews.map(review => <div key={review.id} className="flex-shrink-0 w-[280px] sm:w-[300px] snap-start" data-id="element-9364" data-mp="tejypaijea01">
                <div className={`rounded-xl p-6 shadow-sm border flex flex-col h-full ${dk.card}`} data-id="element-9365" data-mp="1m3h5ohhaskdh">
                  <div className="flex items-start gap-4 mb-4" data-id="element-9366" data-mp="1h57boa7b7ptg">
                    <img src={review.bookCover} alt={review.bookTitle} className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0" loading="lazy" onError={handleImgError} data-id="element-9367" data-mp="1ccmfx34uf2na" />
                    <div data-id="element-9368" data-mp="202gr7g9ikj5f">
                      <div className="flex text-yellow-400 mb-1" aria-label={`${review.rating} out of 5 stars`} data-id="element-9369" data-mp="2015rpsn3pxk4">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-3.5 w-3.5 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} data-id="element-9370" data-mp="3kkspfa3hrl49" data-mpi={i} />)}
                      </div>
                      <h3 className={`font-bold text-sm line-clamp-2 ${dk.textPrimary}`} data-id="element-9371" data-mp="3aopqed3xs2lj">
                        {review.bookTitle}
                      </h3>
                      <p className={`text-xs ${dk.textSecondary}`} data-id="element-9372" data-mp="31o9p9aao17ju">
                        by {review.author}
                      </p>
                    </div>
                  </div>
                  <blockquote className={`flex-1 text-sm italic mb-4 ${dk.textSecondary}`} data-id="element-9373" data-mp="zsh5r57a7xx1">
                    "{review.quote}"
                  </blockquote>
                  <div className="mt-auto flex items-center justify-between" data-id="element-9374" data-mp="1evh75xvg0oso">
                    <div className="flex flex-col" data-id="element-9375" data-mp="1a01wfesqf90h">
                      <span className={`text-xs font-medium ${dk.textPrimary}`} data-id="element-9376" data-mp="1npn8s6c10pzj">
                        {review.reviewerName}
                      </span>
                      <span className={`text-[10px] ${dk.textMuted}`} data-id="element-9377" data-mp="1ixvpmgv79mjf">
                        {review.date}
                      </span>
                    </div>
                    <Link to={content.routes.book(review.bookId)} className={`text-xs font-medium ${rt.accentLink}`} data-id="element-9378" data-mp="1sprohu541gop">
                      View Book →
                    </Link>
                  </div>
                </div>
              </div>)}
          </Carousel>
        </div>
      </section>

      <section className={`py-12 ${dk.surface}`} data-id="element-9379" data-mp="894yamp8se6d">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9380" data-mp="8ag7x35dcg5e">
          <SectionHeader title="Featured Merchandise" icon={ShoppingBagIcon} action={{
          label: 'View all',
          to: '/browse?type=merch'
        }} data-id="element-9381" data-mp="wul9ac93kaqc" />
          <Carousel label="Featured merchandise" data-id="element-9382" data-mp="16jv43zvbs7i5">
            {data.featuredMerch.map(product => <div key={product.id} className="snap-start" data-id="element-9383" data-mp="1al861yvarqjs">
                <FeaturedMerchCard product={product} data-id="element-9384" data-mp="1akom6cm36s34" />
              </div>)}
          </Carousel>
        </div>
      </section>

      <section className={`py-12 ${dk.page}`} data-id="element-9385" data-mp="iu0nd77vxysm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9386" data-mp="iutyekdvnagk">
          <SectionHeader title="Featured Book Boxes" icon={BoxIcon} action={{
          label: 'View all',
          to: '/book-boxes'
        }} data-id="element-9387" data-mp="mvm9lj6b7pfh" />
          <Carousel label="Featured book boxes" data-id="element-9388" data-mp="rsm6b3h9rftx">
            {data.featuredBookBoxes.map(box => <div key={box.id} className="snap-start" data-id="element-9389" data-mp="23a5d15u260x8">
                <FeaturedBookBoxCard box={box} data-id="element-9390" data-mp="238udji7nbfbx" />
              </div>)}
          </Carousel>
        </div>
      </section>

      <section className={`py-12 ${dk.surface}`} data-id="element-9391" data-mp="2dfwvvwt5slr3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9392" data-mp="28g0qbt4tcm9f">
          <SectionHeader title="Upcoming Events" icon={CalendarIcon} action={{
          label: 'View all',
          to: '/events'
        }} data-id="element-9393" data-mp="23o0wmifc6pla" />
          <Carousel label="Upcoming events" data-id="element-9394" data-mp="1zmfdifc7lf2n">
            {data.upcomingEvents.map(event => <div key={event.id} className="snap-start" data-id="element-9395" data-mp="1uovyhd7osy9n">
                <EventTeaserCard event={event} data-id="element-9396" data-mp="1po88muv72nh0" />
              </div>)}
          </Carousel>
        </div>
      </section>

      <section className={`py-12 ${dk.page}`} data-id="element-9397" data-mp="1kpwy0hx2s8xq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-9398" data-mp="1go336q9k9z5h">
          <SectionHeader title={content.home.troveTitle} icon={BookOpenIcon} action={{
          label: 'View all articles',
          to: '/trove'
        }} data-id="element-9399" data-mp="1gpwam7s00nej" />
          {data.troveArticles.length > 0 && <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9400" data-mp="colaoslcxrkk">
              {data.troveArticles.slice(0, 3).map(article => <TroveArticleCard key={article.id} article={article} data-id="element-9401" data-mp="8lydsd9txggi" />)}
            </div>}
        </div>
      </section>

      <NewsletterSection content={content.newsletter} data-id="element-9402" data-mp="mketuutl0jbb" />

      <VendorCTASection content={content.vendorCta} data-id="element-9403" data-mp="hopno8n24461" />
    </div>;
};
export default Home;